import { useMemo } from "react";
import {
  deviceStatus, byUrgency, countStatuses, health, backupSummary, timeAgoEpoch,
} from "./dashboardHelpers.js";
import { Section, KpiTile, StatusDot, StatusBadge } from "./DashboardParts.jsx";
import { NasCard, GatewayCard } from "./DeviceCards.jsx";

// ══════════════════════════════════════════════════════
//  Onglet Réseau & stockage — UDM puis NAS, liste à gauche,
//  fiche détaillée à droite (NAS : tableau complet des sauvegardes).
//  `selected` ({kind,id}) est piloté par la page pour permettre d'arriver
//  ici depuis la liste d'incidents de la Vue d'ensemble.
// ══════════════════════════════════════════════════════

function BackupTable({ jobs }) {
  if (!jobs.length) return null;
  const th = { textAlign: "left", fontSize: 11, color: "var(--text-3)", fontWeight: 600, padding: "6px 10px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
  const td = { fontSize: 12.5, padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--text-1)", verticalAlign: "top" };
  const sorted = [...jobs].sort((a, b) => health(a.status).rank - health(b.status).rank || (b.lastRunEpoch || 0) - (a.lastRunEpoch || 0));
  return (
    <div className="itm-card" style={{ marginTop: 12 }}>
      <div className="card-title-row">
        <div>
          <div className="card-title">Détail des sauvegardes</div>
          <div className="card-sub">Hyper Backup et Active Backup for Business</div>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead>
            <tr>{["Statut", "Tâche", "Type", "Dernière exécution", "Prochaine exécution", "Durée", "Résultat DSM"].map(h => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {sorted.map((j, i) => (
              <tr key={`${j.jobName}-${i}`}>
                <td style={td}><StatusBadge status={j.status} /></td>
                <td style={{ ...td, fontWeight: 600 }}>{j.jobName || "—"}</td>
                <td style={td}>{j.source || "—"}</td>
                <td style={td}>
                  {j.lastRunLabel || "—"}
                  {j.lastRunEpoch ? <div style={{ fontSize: 11, color: "var(--text-3)" }}>{timeAgoEpoch(j.lastRunEpoch)}</div> : null}
                </td>
                <td style={td}>{j.nextRunLabel || "—"}</td>
                <td style={td}>{j.durationLabel || "—"}</td>
                <td style={{ ...td, color: "var(--text-3)", fontSize: 11.5 }}>{j.rawStatus || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function NetworkTab({ data, selected, onSelect }) {
  const { nas, gateways, loading, errors } = data;

  const gwSorted  = useMemo(() => [...gateways].sort(byUrgency(deviceStatus, g => g.name)), [gateways]);
  const nasSorted = useMemo(() => [...nas].sort(byUrgency(deviceStatus, n => n.name)), [nas]);

  const current = useMemo(() => {
    if (selected?.kind === "udm") { const g = gateways.find(x => x.id === selected.id); if (g) return { kind: "udm", item: g }; }
    if (selected?.kind === "nas") { const n = nas.find(x => x.id === selected.id); if (n) return { kind: "nas", item: n }; }
    if (gwSorted[0])  return { kind: "udm", item: gwSorted[0] };
    if (nasSorted[0]) return { kind: "nas", item: nasSorted[0] };
    return null;
  }, [selected, gateways, nas, gwSorted, nasSorted]);

  const c = countStatuses([...gateways.map(deviceStatus), ...nas.map(deviceStatus)]);
  const total = gateways.length + nas.length;
  const error = [errors.monitoring].filter(Boolean).join(" · ");

  const Row = ({ kind, item, sub }) => (
    <button className={`dash-row-btn ${current?.kind === kind && current.item.id === item.id ? "active" : ""}`} onClick={() => onSelect({ kind, id: item.id })}>
      <StatusDot status={deviceStatus(item)} size={9} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
        <span style={{ display: "block", fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.companyName || "—"}</span>
      </span>
      <span style={{ fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>{sub}</span>
    </button>
  );

  const nasSub = (n) => {
    const { latest } = backupSummary(n.backupJobs);
    return latest ? timeAgoEpoch(latest.lastRunEpoch) : (n.storagePercent != null ? `${n.storagePercent} %` : "");
  };

  return (
    <div>
      <div className="dash-metrics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
        <KpiTile label="UDM" value={gateways.length} />
        <KpiTile label="NAS" value={nas.length} />
        <KpiTile label="Avertissements" value={c.WARNING} color={c.WARNING ? health("WARNING").color : "var(--text-3)"} />
        <KpiTile label="Critiques" value={c.CRITICAL} color={c.CRITICAL ? health("CRITICAL").color : "var(--text-3)"} />
      </div>

      <div className="itm-card">
        <div className="card-title-row">
          <div>
            <div className="card-title">Réseau & stockage</div>
            <div className="card-sub">UDM puis NAS — sélectionnez un appareil pour voir sa fiche complète</div>
          </div>
        </div>
        <Section loading={loading} error={error} count={total} empty="Aucune passerelle ni NAS enregistré.">
          <div className="dash-split" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 14, alignItems: "start" }}>
            <div style={{ maxHeight: 640, overflowY: "auto" }}>
              {gwSorted.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", padding: "4px 10px", textTransform: "uppercase", letterSpacing: ".04em" }}>UDM ({gwSorted.length})</div>}
              {gwSorted.map(g => <Row key={g.id} kind="udm" item={g} sub={g.latencyMs != null ? `${g.latencyMs} ms` : ""} />)}
              {nasSorted.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", padding: "10px 10px 4px", textTransform: "uppercase", letterSpacing: ".04em" }}>NAS ({nasSorted.length})</div>}
              {nasSorted.map(n => <Row key={n.id} kind="nas" item={n} sub={nasSub(n)} />)}
            </div>

            <div style={{ minWidth: 0 }}>
              {current?.kind === "udm" && <GatewayCard gateway={current.item} />}
              {current?.kind === "nas" && (
                <>
                  <NasCard nas={current.item} />
                  <BackupTable jobs={current.item.backupJobs || []} />
                </>
              )}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}